import barcodeGuide from "@assets/images/barcode_guide.svg";
import barcodeIcon from "@assets/images/barcode.svg";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import makeStyles from "@material-ui/core/styles/makeStyles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CardTitle from "@saleor/components/CardTitle";
import { maybe } from "@saleor/misc";
import {
  DeliveryTypeEnum,
  OrderEventsEnum,
  OrderVerticalType,
  SmopOrderStatusEnum
} from "@saleor/types/globalTypes";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import React, { Fragment, useEffect, useRef, useState } from "react";

import { StoreOrderDetails_storeOrder } from "../../types/StoreOrderDetails";

const useStyles = makeStyles(
  () => ({
    afterCheck: {
      "&:after": {
        backgroundColor: "#fff",
        content: "''",
        height: "3px",
        left: "7px",
        position: "absolute",
        top: "14px",
        width: "4px"
      }
    },
    avatar: {
      border: "1px solid #ccc",
      borderRadius: "5px",
      height: "40px",
      marginBottom: "10px",
      marginRight: "13px",
      overflow: "hidden",
      padding: "5px",
      width: "40px"
    },
    avatarImg: {
      height: "100%",
      maxHeight: "30px",
      maxWidth: "30px",
      objectFit: "cover",
      width: "100%"
    },
    barcodeContent: {
      "@media (max-width: 900px)": {
        padding: "0"
      },
      width: "800px"
    },
    barcodeGuide: {
      "@media (max-width: 768px)": {
        padding: "70px 60px"
      },
      left: "0",
      padding: "45px 60px",
      position: "absolute",
      right: "0",
      zIndex: 1
    },
    barcodeGuideImg: {
      width: "100%"
    },
    beforeCheck: {
      "&:before": {
        backgroundColor: "#fff",
        content: "''",
        height: "12px",
        left: "11px",
        position: "absolute",
        top: "5px",
        width: "3px"
      }
    },
    brandName: {
      marginTop: "-8px"
    },
    cameraButton: {
      alignItems: "center",
      cursor: "pointer",
      paddingLeft: "5px",
      position: "absolute",
      right: "12px",
      top: "6px",
      zIndex: 9
    },
    cameraButtonImg: {
      width: "25px"
    },
    cameraDialog: {
      "& .MuiDialog-paperWidthMd": {
        maxWidth: "850px"
      }
    },
    cameraDisabled: {
      cursor: "not-allowed",
      opacity: "0.5",
      pointerEvents: "none"
    },
    checkMark: {
      WebkitTransform: "rotate(45deg)",
      background: "#3AB74E",
      borderRadius: "50%",
      display: "inline-block",
      height: "22px",
      marginBottom: "5px",
      msTransform: "rotate(45deg)",
      transform: "rotate(45deg)",
      width: "22px"
    },
    contentTop: {
      display: "-webkit-box"
    },
    parcelCamera: {
      maxHeight: "300px"
    },
    parcelNo: {
      marginRight: "10px"
    },
    parcelNoContent: {
      display: "flex",
      width: "100%"
    },
    requiredLabel: {
      "& button": {
        "& span": {
          "&:before": {
            display: "none"
          }
        }
      },
      "& span": {
        "&:before": {
          color: "red",
          content: "'*'",
          marginLeft: "-12px",
          position: "absolute"
        }
      }
    },
    searchForm: {
      "& input[type=text]": {
        paddingRight: "52px"
      },
      display: "flex",
      flex: 1,
      position: "relative"
    },
    textField: {
      padding: "0px !important"
    }
  }),
  {
    name: "StoreOrderParcelID"
  }
);

export interface StoreOrderParcelIDProps {
  onSubmitOpsScan: (data: any) => void;
  onSubmitRunnerScan: (data: any) => void;
  onSubmitParcelNo: (data: any) => void;
  storeOrder: StoreOrderDetails_storeOrder;
  userType: string;
  disableAction: boolean;
  enableManageMallActions: boolean;
}

const StoreOrderParcelID: React.FC<StoreOrderParcelIDProps> = props => {
  const {
    storeOrder,
    onSubmitParcelNo,
    onSubmitRunnerScan,
    onSubmitOpsScan,
    userType,
    disableAction,
    enableManageMallActions
  } = props;
  const classes = useStyles(props);

  const [parcelValue, setParcelValue] = React.useState("");
  const [errorTextField, setErrorTextField] = React.useState("");
  const [barcodeScanner, setBarcodeScanner] = useState<Html5Qrcode | null>(
    null
  );
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const inputProps = {
    style: { padding: "8px" }
  };

  const handleSubmitParcel = () => {
    if (parcelValue === "") {
      setErrorTextField("This field should not be empty");
    } else {
      setErrorTextField("");

      if (enableManageMallActions) {
        if (
          maybe(() => storeOrder.smopStatus) ===
            SmopOrderStatusEnum.ACKNOWLEDGED &&
          maybe(() => storeOrder.order.verticalType) ===
            OrderVerticalType.GENERAL_MERCHANDISE
        ) {
          onSubmitParcelNo(parcelValue);
        } else if (maybe(() => storeOrder.smopStatus) === "READY") {
          onSubmitRunnerScan(parcelValue);
        } else {
          onSubmitOpsScan(parcelValue);
        }
      } else {
        switch (userType) {
          case "STORE_ADMIN":
            onSubmitParcelNo(parcelValue);
            break;
          case "RUNNER":
            onSubmitRunnerScan(parcelValue);
            break;
          case "OMI_OPS":
            if (
              maybe(() => storeOrder.smopStatus) ===
                SmopOrderStatusEnum.ACKNOWLEDGED &&
              maybe(() => storeOrder.order.verticalType) ===
                OrderVerticalType.GENERAL_MERCHANDISE
            ) {
              onSubmitParcelNo(parcelValue);
            } else if (maybe(() => storeOrder.smopStatus) === "READY") {
              onSubmitRunnerScan(parcelValue);
            } else {
              onSubmitOpsScan(parcelValue);
            }
            break;
          default:
            break;
        }
      }
    }

    setParcelValue("");
  };

  const onEnterPress = e => {
    if (e.key === "Enter") {
      handleSubmitParcel();
      setParcelValue("");
    }
  };

  const checkDisableForOPS = () => {
    if (
      storeOrder &&
      storeOrder.smopStatus !== "COLLECTED" &&
      storeOrder &&
      storeOrder.smopStatus !== "READY" &&
      !(
        maybe(() => storeOrder.smopStatus) ===
          SmopOrderStatusEnum.ACKNOWLEDGED &&
        maybe(() => storeOrder.order.verticalType) ===
          OrderVerticalType.GENERAL_MERCHANDISE
      )
    ) {
      return true;
    } else {
      return false;
    }
  };

  const checkDisable = () => {
    if (enableManageMallActions) {
      return checkDisableForOPS();
    }

    switch (userType) {
      case "OMI_OPS":
        return checkDisableForOPS();
      case "STORE_ADMIN":
        if (storeOrder && storeOrder.smopStatus !== "ACKNOWLEDGED") {
          return true;
        } else {
          return false;
        }
      case "RUNNER":
        if (storeOrder && storeOrder.smopStatus !== "READY") {
          return true;
        } else {
          return false;
        }
      default:
        return true;
    }
  };

  const checkVerified = () => {
    const checkOmi =
      storeOrder &&
      storeOrder.events.some(
        ({ type }) => type === OrderEventsEnum.STORE_ORDER_SCANNED_BY_OMI_OPS
      );

    const checkRunner =
      storeOrder &&
      storeOrder.events.some(
        ({ type }) => type === OrderEventsEnum.STORE_ORDER_SCANNED_BY_RUNNER
      );

    if (enableManageMallActions) {
      return checkOmi;
    }

    switch (userType) {
      case "OMI_OPS":
        if (checkOmi) {
          return true;
        } else {
          return false;
        }
      case "RUNNER":
        if (checkRunner) {
          return true;
        } else {
          return false;
        }
      default:
        return false;
    }
  };

  const [open, setOpen] = useState(false);

  useEffect(() => {
    const initializeScanner = () => {
      if (
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === "function"
      ) {
        setTimeout(() => {
          const readerElementId = "parcelCamera";

          const readerElement = document.querySelector("#parcelCamera");

          if (readerElement) {
            const scanner = new Html5Qrcode(readerElementId);

            const detectorConfig = {
              formatsToSupport: [
                Html5QrcodeSupportedFormats.CODE_128,
                Html5QrcodeSupportedFormats.EAN_13,
                Html5QrcodeSupportedFormats.EAN_8
              ],
              fps: 10,
              videoConstraints: {
                height: 300,
                width: 800
              }
            };

            const cameraConfig = {
              facingMode: "environment"
            };

            const onScanSuccess = (decodedText: string) => {
              setParcelValue(decodedText);
              setOpen(false);
            };

            const onScanError = (error: any) => error;

            scanner
              .start(cameraConfig, detectorConfig, onScanSuccess, onScanError)
              .then(() => {
                setBarcodeScanner(scanner);
                scannerRef.current = scanner;
              })
              .catch(onScanError);
          }
        }, 1000);
      }
    };

    if (open && !barcodeScanner && !scannerRef.current) {
      initializeScanner();
    }

    return () => {
      if (scannerRef.current && scannerRef.current.isScanning) {
        scannerRef.current
          .stop()
          .then(() => {
            if (scannerRef.current) {
              scannerRef.current.clear();
            }
          })
          .catch(error => error);

        setBarcodeScanner(null);
        scannerRef.current = null;
      }
    };
  }, [open, barcodeScanner]);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Card>
      <CardTitle
        title="Associated Parcel ID"
        className={
          storeOrder?.order.shippingMethod.deliveryType ===
            DeliveryTypeEnum.NEXT_DAY ||
          storeOrder?.order.shippingMethod.deliveryType ===
            DeliveryTypeEnum.SAME_DAY ||
          storeOrder?.order.shippingMethod.deliveryType ===
            DeliveryTypeEnum.NATIONWIDE_DELIVERY
            ? classes.requiredLabel
            : null
        }
        toolbar={
          checkDisable() ? null : (
            <Button
              id="submitParcelBtn"
              color={disableAction ? "default" : "primary"}
              onClick={handleSubmitParcel}
              disabled={disableAction}
            >
              {userType === "STORE_ADMIN" ||
              ((userType === "OMI_OPS" || enableManageMallActions) &&
                maybe(() => storeOrder.smopStatus) ===
                  SmopOrderStatusEnum.ACKNOWLEDGED &&
                maybe(() => storeOrder.order.verticalType) ===
                  OrderVerticalType.GENERAL_MERCHANDISE)
                ? "ASSOCIATE"
                : "VALIDATE"}
            </Button>
          )
        }
      />

      <CardContent id="parcelIDCardContent">
        {storeOrder?.parcelNo ? (
          <div className={classes.contentTop}>
            <div className={classes.avatar}>
              <img
                className={classes.avatarImg}
                src={storeOrder?.tenant?.brand?.logo?.url}
              />
            </div>
            <div>
              <div className={classes.parcelNoContent}>
                <Typography variant="subtitle1" className={classes.parcelNo}>
                  {storeOrder.parcelNo}
                </Typography>
                {checkVerified() ? (
                  <span
                    className={`${classes.checkMark} ${classes.beforeCheck} ${classes.afterCheck}`}
                  ></span>
                ) : null}
              </div>
              <div className={classes.brandName}>
                {storeOrder.tenant.brand.name}
              </div>
            </div>
          </div>
        ) : (
          <Typography variant="subtitle1">No parcel ID to be shown</Typography>
        )}
        <div className={classes.searchForm}>
          <TextField
            id="parcelIdTxt"
            fullWidth
            error={!!errorTextField}
            helperText={errorTextField}
            disabled={checkDisable() || disableAction}
            className={classes.textField}
            inputProps={inputProps}
            value={parcelValue}
            onChange={e => setParcelValue(e.target.value.replace(/\s/g, ""))}
            onKeyPress={onEnterPress}
            InputProps={{
              placeholder: "Parcel ID"
            }}
          />
          <div
            className={
              classes.cameraButton +
              (checkDisable() || disableAction
                ? " " + classes.cameraDisabled
                : "")
            }
            onClick={handleClickOpen}
          >
            <img
              src={barcodeIcon}
              alt="Barcode Icon"
              className={classes.cameraButtonImg}
            />
          </div>
        </div>
      </CardContent>
      <Dialog
        id="scanParcelIDDialog"
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        className={classes.cameraDialog}
      >
        <DialogTitle id="alert-dialog-title">Scan Parcel ID</DialogTitle>
        <DialogContent className={classes.barcodeContent}>
          <Fragment>
            <div className={classes.barcodeGuide}>
              <img className={classes.barcodeGuideImg} src={barcodeGuide} />
            </div>
            <div
              id="parcelCamera"
              className={classes.parcelCamera}
              style={{ width: "100%" }}
            ></div>
          </Fragment>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

StoreOrderParcelID.displayName = "ParcelID";
export default StoreOrderParcelID;
